import { ref } from 'vue'

import { getI18n } from '@/plugins/i18n'
import { isNotEmpty } from '@/lib/utils'

import router, { menuRoutesEnum } from '@/router'

import { useDialog } from '@/composables/dialog'
import { useNotify } from '@/composables/notify'
import { usePartialSave } from '@/composables/partial-save'

import { usePortalStore } from '@/stores/portal.store'
import { useRegistryStore } from '@/stores/registry.store'
import { useGarageStore } from '@/stores/garage.store'
import { useWebSiteStore } from '@/stores/web-site.store'

import { usePriceService } from '@/services/price.service'
import { useDocumentService } from '@/services/document.service'

export const useDocumentIdentifiers = () => {
  const i18n = getI18n()
  const registryStore = useRegistryStore()
  const portalStore = usePortalStore()
  const garageStore = useGarageStore()
  const webSiteStore = useWebSiteStore()
  const { genericDialog, customDialog } = useDialog()
  const { getIncludesVatValue } = usePriceService()
  const { isSaveDisabled } = usePartialSave()
  const { currentVehicleRevisionDueDate, setVehicleNextRevisionDueDate } = useDocumentService()

  const isVehicleSaveDisabled = ref(true)
  const disabledVehicleFields = [['plate_number', 'vin'], 'name', 'supplier_brand_ref']
  const addressRequiredFields = ['country', ['state', 'state_text'], 'zip_code', ['city', 'city_text'], 'street']

  const getDocumentDetailsAsync = async(id, apiMethod, baseData, config = {}) => {
    const res = await apiMethod(id, null, { onNotFound: config?.onNotFound })

    baseData.customer = baseData.customer_id ? (await registryStore.getOrFetchCustomersAsync()).find(i => i.id === baseData.customer_id) : {}
    baseData.vehicle = baseData.vehicle_id ? (await registryStore.getOrFetchVehiclesAsync()).find(i => i.id === baseData.vehicle_id) : {}
    setVehicleNextRevisionDueDate(baseData)
    baseData.delivery_address = baseData.delivery_address_ref ? (await registryStore.getOrFetchSubjectContactsAsync(true)).find(i => i.id === baseData.delivery_address_ref) : {}

    const details = { ...baseData, ...res.data, vehicle: { ...baseData.vehicle, ...res.data.vehicle } }

    return { value: details }
  }

  const onDocumentInitNewRowAsync = async(e, nextIdFunction) => {
    if (nextIdFunction) {
      const res = await nextIdFunction(e.data)
      e.data.number_mnemonic = res.data['number_mnemonic']
      e.data.series = res.data['series']
    }
    e.data.includes_vat = getIncludesVatValue()
  }

  const setDocumentFieldReadOnly = (e, customExcludedFields = []) => {
    if (e.parentType !== 'dataRow' || e.editorOptions?.disabled) return

    const excludedFields = ['notes', 'payment_type_ref']
    const currentExcludedFields = [...new Set([...excludedFields, ...customExcludedFields])]

    if (e.parentType === 'dataRow' && !currentExcludedFields.includes(e.dataField)) {
      e.editorOptions.disabled = e.row.data?.rows_count > 0
    }
  }

  const loadDocumentDetailsAsync = async(api, rowData, refs) => {
    const docDetails = await getDocumentDetailsAsync(
      rowData.data.id,
      api,
      rowData.data,
      { onNotFound: () => rowData.component?.cancelEditData() }
    )

    const data = docDetails.value
    rowData.data = data

    const {
      currentVehicleFuelType,
      currentCustomerTypeId,
      currentCountryId,
      currentCustomerVatCountryId,
      currentStateId,
      includesVat,
      currentCarService,
      formInstance
    } = refs

    currentVehicleFuelType.value = data?.vehicle?.fuel_type ?? null
    currentCustomerTypeId.value = data?.customer?.subject_type_ref ?? null
    currentCountryId.value = data?.customer?.address?.country ?? null
    currentCustomerVatCountryId.value = data?.customer?.vat_country_id ?? null
    currentStateId.value = data?.customer?.address?.state ?? null
    includesVat.value = getIncludesVatValue(data?.customer)

    const garageReason = garageStore.garageReasons.find(i => i.id === data?.garage_reason_ref)
    const interventionType = garageStore.interventionTypes.find(i => i.id === garageReason?.intervention_type_ref)

    if (interventionType?.has_car_service && data?.vehicle_id) {
      const nextCarService = await registryStore.api.getVehicleNextCarServiceAsync(data.vehicle_id)
      currentCarService.value = nextCarService.data
    }

    formInstance.value.repaint()
  }

  const updateSeriesAndNumberMnemonicAsync = async(data, editors, nextIdFunction) => {
    if (Object.prototype.hasOwnProperty.call(data, 'sale_document_type') && data.sale_document_type == null) return

    await nextIdFunction(data).then(res => {
      editors['number_mnemonic'].option('value', res.data.number_mnemonic)
      editors['series'].option('value', res.data.series)
    })
  }

  const onDocumentEditorPreparing = (e, editors, nextIdFunction, gridRef, customExcludedFields = [], documentType = null) => {
    if (e.parentType === 'dataRow' && e.dataField === 'date') {
      e.editorOptions.readOnly = !!e.row.data?.id
      e.editorOptions.onInitialized = (arg) => { editors['series'] = arg.component }
    }
    if (e.parentType === 'dataRow' && e.dataField === 'series') {
      e.editorOptions.onInitialized = (arg) => { editors['series'] = arg.component }
    }
    if (e.parentType === 'dataRow' && e.dataField === 'number_mnemonic') {
      e.editorOptions.onInitialized = (arg) => { editors['number_mnemonic'] = arg.component }
    }

    const seriesRecalculationFields = ['date', 'supplier_brand_ref']
    if (e.parentType === 'dataRow' && seriesRecalculationFields.includes(e.dataField)) {
      if (!e.row.data?.id) {
        const defaultValueChanged = e.editorOptions.onValueChanged
        e.editorOptions.onValueChanged = async(arg) => {
          e.promise = updateSeriesAndNumberMnemonicAsync({ ...e.row.data, ...{[e.dataField]: arg.value} }, editors, nextIdFunction)
          defaultValueChanged(arg)
        }
      }
    }

    if ([portalStore.documentTypesEnum.GARAGE_WORKSHEET, portalStore.documentTypesEnum.GARAGE_QUOTE].includes(documentType)) {
      if (e.parentType === 'dataRow' && (e.dataField === 'vehicle.vin' || e.dataField === 'vehicle.plate_number' || e.dataField === 'vehicle.name' || e.dataField === 'vehicle.supplier_brand_ref' || e.dataField === 'vehicle.km' || e.dataField === 'garage_reason_ref')) {
        isVehicleSaveDisabled.value = isSaveDisabled('vehicle', disabledVehicleFields, gridRef, addressRequiredFields)
        const defaultValueChanged = e.editorOptions.onValueChanged
        e.editorOptions.onValueChanged = (args) => {
          defaultValueChanged(args)
          isVehicleSaveDisabled.value = isSaveDisabled('vehicle', disabledVehicleFields, gridRef, addressRequiredFields)
        }
      }
    }

    if (e.parentType === 'dataRow' && e?.isOnForm) { setDocumentFieldReadOnly(e, customExcludedFields) }
  }

  const checkAndAskForUpdateCustomerEmailOrMobile = async(data, check_tax_code = false) => {
    if (data && data?.customer && typeof data?.customer === 'object') {
      const currentCustomer = (await registryStore.getOrFetchCustomersAsync()).find(c => c.id === (data.customer_id || data.customer?.id))
      const hasAnyValue = isNotEmpty(data?.customer?.mobile) || isNotEmpty(data?.customer?.email) || (check_tax_code && isNotEmpty(data?.customer?.tax_code))
      const isChanged = currentCustomer?.mobile !== data?.customer?.mobile || currentCustomer?.email !== data?.customer?.email || (check_tax_code && currentCustomer?.tax_code !== data?.customer?.tax_code)
      if (hasAnyValue && isChanged) {
        const mobileToUpdate = (data.mobile && data.mobile !== data?.customer?.mobile) || (!data.mobile && data?.customer?.mobile)
        const emailToUpdate = (data.email && data.email !== data?.customer?.email) || (!data.email && data?.customer?.email)
        const taxCodeToUpdate = check_tax_code && ((data.tax_code && data.tax_code !== data?.customer?.tax_code) || (!data.tax_code && data?.customer?.tax_code))
        if (mobileToUpdate || emailToUpdate || taxCodeToUpdate) {
          genericDialog(
            null,
            i18n.t('common.updateCustomerInfo'),
            { cancel: i18n.t('common.no'), ok: i18n.t('common.yes'), persistent: true }
          ).onOk(async() => {
            const new_data = { type: portalStore.documentPriceTypes.CUSTOMER }
            if (mobileToUpdate) { new_data.mobile = data.customer.mobile }
            if (emailToUpdate) { new_data.email = data.customer.email }
            if (taxCodeToUpdate) { new_data.tax_code = data.customer.tax_code }

            await registryStore.api.updateSubjectAsync(-currentCustomer.subject, new_data)
          })
        }
      }
    }
  }

  const checkCustomerPrivacyAsync = async(customerId) => {
    if (customerId) {
      const { notifyWarning } = useNotify()
      const res = (await registryStore.api.checkCustomerPrivacyAsync(customerId))?.data
      if (res?.warning) {
        notifyWarning(i18n.t('error.validation.privacy_check'), {
          timeout: window.config.NOTIFICATION_DURATION,
          actions: [
            {
              label: i18n.t('common.complete_action'), size: 'sm', color: 'black', handler: async() => {
                const route = { path: menuRoutesEnum.SUBJECTS, query: { code: res?.params['subjectCode'], openEdit: true, subjectValidation: portalStore.subjectValidationTypesEnum.PRIVACY, tab: 1 } }
                const resolvedRoute = router.resolve(route)
                window.open(resolvedRoute.href, '_blank')
              }
            }
          ]
        })
      }
    }
  }

  const onDocumentSaving = async(e, selectedDocument, options = {}) => {
    if (e.changes?.[0]?.type === 'remove') { selectedDocument.value = null }

    if (options?.isOwnerSaveDisabled) { options.isOwnerSaveDisabled.value = true }
    if (options?.isVehicleSaveDisabled) { options.isVehicleSaveDisabled.value = true }

    for (const change of e.changes) {
      if (!options?.requireCustomerPrivacyCheck) {
        const customerId = change.data?.customer?.id
        await checkCustomerPrivacyAsync(customerId)
      }

      if (change.data && change.data?.vehicle && typeof change.data?.vehicle === 'object') {
        if (change.data?.vehicle?.km) {
          change.data.km = change.data.vehicle.km
        }
        if (change.data?.vehicle?.next_revision_due_date === change.data?.vehicle?.revision_due_date) {
          delete change.data.vehicle.next_revision_due_date
        }
        if (change.data?.vehicle?.next_revision_due_date) {
          change.data.vehicle.revision_due_date = change.data.vehicle?.next_revision_due_date
        }
        if (change.data?.vehicle?.next_revision_due_date && currentVehicleRevisionDueDate.value) {
          if (change.data?.vehicle?.next_revision_due_date < currentVehicleRevisionDueDate.value) {
            const keepDate = await customDialog(i18n.t('common.revisionNextDate'), i18n.t('error.validation.nextRevisionMustBeGreater'), i18n.t('common.yes'), i18n.t('common.no'))
            if (!keepDate) {
              delete change.data.vehicle.next_revision_due_date
              delete change.data.vehicle.revision_due_date
            }
          }
        }
      }

      if (change.data && change.data?.customer && typeof change.data?.customer === 'object') {
        change.data.customer = {id: change.data?.customer?.id || selectedDocument.value?.customer?.id || selectedDocument.value?.customer_id, ...(change.data?.customer || {})}
      }

      await checkAndAskForUpdateCustomerEmailOrMobile(change.data, options?.check_tax_code)
    }

    return true
  }

  const onDocumentRowUpdating = (e, selectedDocument) => { selectedDocument.value = { ...e.oldData, ...e.newData } }

  const getDocumentTags = (isPaid, includesVat, selectedInterventionTypeId = null) => {
    const tags = []
    if (isPaid) {
      tags.push({id: tags.length + 1, name: isPaid.value ? i18n.t('common.paid') : i18n.t('common.notPaid'), color: isPaid.value && webSiteStore.paidColor})
    }
    if (selectedInterventionTypeId) {
      const interventionType = garageStore.getLocalizedInterventionTypes().find(x => x.id === selectedInterventionTypeId.value)
      tags.push({id: tags.length + 1, name: interventionType?.name, color: interventionType?.color})
    }
    if (includesVat) {
      tags.push({id: tags.length + 1, name: includesVat.value ? i18n.t('common.withVAT') : i18n.t('common.withoutVAT'), color: includesVat.value ? webSiteStore.withVATColor : webSiteStore.withoutVATColor})
    }
    return tags
  }

  return {
    isVehicleSaveDisabled,
    checkAndAskForUpdateCustomerEmailOrMobile,
    checkCustomerPrivacyAsync,
    getDocumentDetailsAsync,
    getDocumentTags,
    loadDocumentDetailsAsync,
    onDocumentEditorPreparing,
    onDocumentInitNewRowAsync,
    onDocumentRowUpdating,
    onDocumentSaving,
    updateSeriesAndNumberMnemonicAsync,
  }
}
